import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';
import { WidgetView } from 'utilitys/reporting/widgetViewModeUtils';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useQueryGetCriticalityWeightScurveGraph } from 'api/queries/getCriticalityWeightScurveGraph.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { getDecodedValue } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramProgressOverview/programProgressOverview.utils';
import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { graphLineOptionsDeflates } from 'components/widgets/SCurve/Scurve.utils';
import { useAnnotationToggle } from 'components/widgets/SCurve/hooks/useAnnotationsToggle';
import { useGraphLineOptions } from 'components/widgets/SCurve/hooks/useGraphLineOptions';

export const useScurveProvider = ({
    componentKey,
    widgetId,
    contractId,
    projectId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    localFilters,
    endpoints,
    widgetViewMode = WidgetView.page,
}) => {
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();
    const isInPinboard = Boolean(widgetId);
    const [graphLineOptions, setGraphLineOptions] = useGraphLineOptions(widgetViewMode, localFilters);
    const [showAnnotations, setShowAnnotations] = useAnnotationToggle(
        widgetViewMode,
        localFilters?.showAnnotations, // restore saved value for report widgets
    );

    const source = isInPinboard
        ? { ...localFilters, showAnnotations, graphLineOptions }
        : { ...globalFilterData, graphLineOptions, showAnnotations };

    const state = {
        project: {
            projectId,
            contractId,
            latestVersionId,
            compareVersionId,
        },
        globalFilter: {
            progressMeasurement: source?.progressMeasurement,
            resource: source?.resource,
            resourceType: source?.resourceType,
            activityCodes: source?.activityCodes,
            milestoneFragnet: source?.milestoneFragnet,
            tags: source?.tags,
            wbs: source?.wbs,
        },
        local: {
            graphLineOptions: graphLineOptionsDeflates(source?.graphLineOptions ?? []),
            showAnnotations: source?.showAnnotations,
        },
    };
    const pillsData = {
        progressMeasurement: source?.progressMeasurement,
        resource: source?.resource,
        resourceType: source?.resourceType,
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
        wbs: source?.wbs,
        graphLineOptions: graphLineOptions.map((i) => i.label),
        isAnnotationShow: source?.showAnnotations?.toString(),
    };

    // create pill data for PinboardWidgetPills component
    const { pillsList: pills } = useGlobalFiltersHook({ source: pillsData, latestVersionId, widgetId });

    // Queries
    const payload = {
        actualMetaDataId: latestVersionId,
        baselineMetaDataId: compareVersionId || latestVersionId,
        graphType: getDecodedValue(source?.progressMeasurement)?.progressMeasurement,
        isPartialCompletion: getDecodedValue(source?.progressMeasurement)?.partialCompletionCredit,
        filter: {
            filters: getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item?.isExclude)
                    .map((i) => i?.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item?.isExclude)
                    .map((i) => i?.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item?.isExclude).map((i) => i?.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                    ?.filter((item) => item?.isExclude)
                    .map((i) => i?.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
                [FilterNames.RESOURCE_NAME_CARD_BOARD]: source?.resource,
                [FilterNames.RESOURCE_TYPE_CARD_BOARD]: source?.resourceType,
            }),
        },
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
    };

    return {
        ui: {
            state: {
                ...state.project,
                ...state.globalFilter,
                ...state.local,
            },
            vars: {
                graphLineOptions,
            },
            isInPinboard,
            pills,
            setters: {
                setGraphLineOptions,
                setShowAnnotations,
            },
        },
        api: {
            scurveGraph: {
                instance: useQueryGetCriticalityWeightScurveGraph(payload, endpoints.getProgressScurveGraph),
                payload,
                endpoint: endpoints.getProgressScurveGraph,
            },
        },
    };
};
